import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import createFetchClient from "openapi-fetch";
import createClient from "openapi-react-query";
import React from "react";
import { useAuth } from "../../app/auth/useAuth.js";
import { useUserData } from "../../app/auth/useUserData.js";
import type { paths } from "./wizard-api.generated.d.ts";

export type FetchFn = (input: Request) => Promise<Response>;

const BACKEND_URL = "/api";

function createOpenapiClient(backendUrl: string, customFetch?: FetchFn) {
  const fetchClient = createFetchClient<paths>({
    baseUrl: `${backendUrl}/embeddable-wizard`,
    ...(customFetch ? { fetch: customFetch } : undefined),
  });

  const reactQueryClient = createClient(fetchClient);

  return { fetchClient, reactQueryClient };
}

const { fetchClient, reactQueryClient } = createOpenapiClient(BACKEND_URL);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      experimental_prefetchInRender: true,
    },
  },
});

const WizardApiClientContext = React.createContext<
  ReturnType<typeof createOpenapiClient>
>({ fetchClient, reactQueryClient });

interface Props {}

export const WizardApiClientProvider = ({
  children,
}: React.PropsWithChildren<Props>) => {
  const { auth } = useAuth();
  const { currentUser } = useUserData();

  const { fetchClient, reactQueryClient } = React.useMemo(() => {
    const customFetch: FetchFn = async request => {
      await auth.updateToken(5);
      const headers = new Headers(request.headers);

      if (currentUser?.id) {
        headers.set("x-selected-user-id", currentUser.id);
      } else {
        headers.delete("x-selected-user-id");
      }

      if (auth.token) {
        headers.set("authorization", `Bearer ${auth.token}`);
      } else {
        headers.delete("authorization");
      }

      const modifiedRequest = new Request(request, {
        headers,
      });

      return await fetch(modifiedRequest);
    };
    return createOpenapiClient(BACKEND_URL, customFetch);
  }, [auth, currentUser?.id]);

  return (
    <WizardApiClientContext.Provider value={{ fetchClient, reactQueryClient }}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </WizardApiClientContext.Provider>
  );
};

export function useWizardApiClient() {
  const {
    fetchClient: fetchWizardApiClient,
    reactQueryClient: reactWizardApiQueryClient,
  } = React.useContext(WizardApiClientContext);

  return {
    fetchWizardApiClient,
    reactWizardApiQueryClient,
  };
}

export function useWizardQuestion({
  body,
  enabled,
}: {
  body: paths["/questions"]["post"]["requestBody"]["content"]["application/json"];
  enabled?: boolean;
}) {
  const { reactWizardApiQueryClient } = useWizardApiClient();

  const { data, refetch } = reactWizardApiQueryClient.useQuery(
    "post",
    "/questions",
    { body },
    {
      gcTime: 0,
      placeholderData: previousData => previousData,
      networkMode: "always",
      enabled,
    }
  );

  return { data, refetch };
}
